import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tag } from "lucide-react";

const NONE = "__none__";

export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const reload = () => api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  return { categories, reload };
};

export const findCategory = (categories, id) => categories.find((c) => c.id === id);

export const CategoryDot = ({ category, testId }) => (
  <span className="inline-flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur"
    data-testid={testId}>
    <span className="h-2 w-2 rounded-full" style={{ background: category.color }} />
    {category.name}
  </span>
);

export const CategorySelect = ({ categories, value, onChange, disabled, testId, required = false }) => (
  <div className="min-w-[140px] flex-1 space-y-1.5">
    <Label className="flex items-center gap-1.5 text-xs"><Tag className="h-3.5 w-3.5" /> Catégorie</Label>
    <Select value={value || (required ? undefined : NONE)} onValueChange={(v) => onChange(v === NONE ? "" : v)} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
      <SelectContent>
        {!required && <SelectItem value={NONE}>Sans catégorie</SelectItem>}
        {categories.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const Chip = ({ active, onClick, color, label, count, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/60 hover:text-foreground"}`}>
    {color && <span className="h-2 w-2 rounded-full" style={{ background: color }} />}
    {label}<span className="opacity-70">({count})</span>
  </button>
);

export const CategoryFilter = ({ categories, images, value, onChange }) => {
  const count = (id) => images.filter((i) => (i.category_id || "") === id).length;
  return (
    <div className="mb-4 flex flex-wrap gap-2" data-testid="category-filter">
      <Chip active={value === null} onClick={() => onChange(null)} label="Toutes" count={images.length} testId="category-filter-all" />
      {categories.map((c) => (
        <Chip key={c.id} active={value === c.id} onClick={() => onChange(c.id)} color={c.color} label={c.name} count={count(c.id)} testId={`category-filter-${c.id}`} />
      ))}
      <Chip active={value === ""} onClick={() => onChange("")} color="#64748b" label="Sans catégorie" count={count("")} testId="category-filter-none" />
    </div>
  );
};

export const posterCategoryId = (categories) => categories.find((c) => /^affiches?$/i.test(c.name))?.id || "";

export const groupImages = (images, categories) => {
  const rank = new Map(categories.map((c, i) => [c.id, i]));
  const key = (img) => rank.get(img.category_id || "") ?? categories.length;
  return images
    .map((img, i) => [img, i])
    .sort((a, b) => key(a[0]) - key(b[0]) || a[1] - b[1])
    .map(([img], i) => ({ ...img, order: i }));
};

export const categoryDuration = (categories, id) => findCategory(categories, id)?.default_duration || null;

export const UncategorizedAlert = ({ count, onShow }) => !count ? null : (
  <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="uncategorized-alert">
    <Tag className="h-4 w-4 shrink-0" />
    <span className="flex-1"><b data-testid="uncategorized-count">{count} image{count > 1 ? "s" : ""} sans catégorie</b> : {count > 1 ? "elles passent" : "elle passe"} sur tous les écrans. Choisissez une catégorie pour {count > 1 ? "les" : "la"} limiter.</span>
    <button onClick={onShow} className="rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-black hover:bg-amber-400" data-testid="uncategorized-show-button">
      Les sélectionner
    </button>
  </div>
);
